import { useEffect, useState } from "react";
import { Button, Card, Cluster, Grid, Stack } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { api } from "./api";
import { formatUtc } from "./format";
import { Flash } from "./Flash";

interface Row {
  id: string;
  kind: string;
  created_at: string | null;
  expires_at: string | null;
  status: string;
  size_gib: number | null;
  encrypted: boolean;
}

interface Report {
  available: boolean;
  reason?: string;
  database?: string;
  retention_days?: number;
  latest_restorable_time?: string | null;
  encrypted?: boolean;
  deletion_protection?: boolean;
  newest_backup_at?: string | null;
  newest_backup_age_hours?: number | null;
  stale?: boolean;
  total?: number;
  manual_count?: number;
  rows?: Row[];
  vault_error?: string | null;
  server?: { restore_points: number | null; error: string | null };
}

export function BackupsPage() {
  const [data, setData] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      setData(await api.get<Report>("/operator/backups"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load backups.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const serverPoints = data?.server?.restore_points;

  return (
    <Stack gap={4}>
      <Cluster gap={3}>
        <TipHeading
          as="h2"
          label="Backups"
          tip="Every database restore point in AWS, when it was taken and when it expires."
        />
        <Button variant="secondary" onClick={() => void load()}>
          Refresh
        </Button>
      </Cluster>
      {error && <Flash tone="error">{error}</Flash>}
      {data && !data.available && <Flash tone="error">Backups unavailable: {data.reason}</Flash>}
      {data?.available && (
        <>
          {data.stale && (
            <Flash tone="error">
              No new database backup for {data.newest_backup_age_hours ?? "an unknown number of"} hours. The hourly job
              should produce one every hour.
            </Flash>
          )}
          {serverPoints === 0 && (
            <Flash tone="error">
              The app server has no backup or image. It can be rebuilt from Terraform and a deploy, but nothing on its
              disk is saved.
            </Flash>
          )}
          {data.vault_error && <Flash tone="error">AWS Backup vault unreadable: {data.vault_error}</Flash>}
          <Grid columns={3}>
            <Card>
              <Stack gap={2}>
                <strong>Point-in-time recovery</strong>
                <span>Restore to any second up to {formatUtc(data.latest_restorable_time ?? "")}</span>
                <small>Window: last {data.retention_days} days</small>
              </Stack>
            </Card>
            <Card>
              <Stack gap={2}>
                <strong>Newest backup</strong>
                <span>{data.newest_backup_at ? formatUtc(data.newest_backup_at) : "None found"}</span>
                <small>
                  {data.total} restore points, {data.manual_count} manual
                </small>
              </Stack>
            </Card>
            <Card>
              <Stack gap={2}>
                <strong>Protection</strong>
                <span>{data.encrypted ? "Encrypted" : "NOT encrypted"}</span>
                <small>Deletion protection {data.deletion_protection ? "on" : "OFF"}</small>
              </Stack>
            </Card>
          </Grid>
          <small>
            Each database restore point is a complete copy of the whole database ({data.database}: platform accounts and
            every organisation's data) at that moment. AWS stores them incrementally, but each one restores in full.
            They are database backups, not server images. Customer files live in S3 with versioning on.
          </small>
          <table>
            <thead>
              <tr>
                <th>Taken (UTC)</th>
                <th>Type</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Size</th>
                <th>Restore point</th>
              </tr>
            </thead>
            <tbody>
              {data.rows?.map((row) => (
                <tr key={`${row.kind}-${row.id}`}>
                  <td>{row.created_at ? formatUtc(row.created_at) : "-"}</td>
                  <td>{row.kind}</td>
                  <td>{row.expires_at ? formatUtc(row.expires_at) : row.kind.startsWith("Manual") ? "Never" : "-"}</td>
                  <td>{row.status}</td>
                  <td>{row.size_gib ? `${row.size_gib} GiB` : "-"}</td>
                  <td>{row.id}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Stack>
  );
}
